import { prisma } from "client";
import { Privacies, Visibility, BookableItemType } from "client";

interface CreateCategoryInput {
  category: any;
  data: any;
  eventId: string;
  languages: any[];
  whiteLabelId: string;
  userId: string;
  performanceId: string;
}

export const linkTicketCategoryToDtcmSections = async (
  ticketCategoryId: string,
  sectionIds: string[]
) => {
  if (!sectionIds.length) return;

  await prisma.dTCMSection.updateMany({
    where: { id: { in: sectionIds } },
    data: { ticketCategoryId },
  });
};

export const findTicketCategoryByDtcmSections = async (sectionIds: string[]) => {
  if (!sectionIds.length) return null;

  return prisma.ticketCategory.findFirst({
    where: {
      dtcmSections: {
        some: { id: { in: sectionIds } },
      },
    },
  });
};

export const createNewPriceCategoryWithTickets = async ({
  category,
  data,
  eventId,
  languages,
  performanceId,
}: CreateCategoryInput) => {
  const newCategory = await prisma.dTCMPriceCategory.create({
    data: {
      dtcmId: String(category.id),
      code: category.code,
      name: category.name,
      maxQtyPerTransaction: category.maxQtyPerTransaction,
      performance: { connect: { id: performanceId } },
      dtcmSections: {
        create: data.sections
          .filter((s: any) => s.categoryId === category.id)
          .map((section: any) => ({
            code: section.code,
            name: section.name,
            capacity: section.capacity,
            holds: section.holds,
            comps: section.comps,
            isGA: section.isGA,
          })),
      },
      dtcmTicketPrices: {
        create: data.ticketPrices
          .filter((p: any) => p.categoryId === category.id)
          .map((price: any) => ({
            priceTypeId: String(price.typeId),
            typeCode: price.typeCode,
            price: price.price,
            name: data.priceTypes.find((pt: any) => pt.id === price.typeId)
              ?.name,
            state: data.priceTypes.find((pt: any) => pt.id === price.typeId)
              ?.state,
          })),
      },
    },
    include: {
      dtcmSections: true,
      dtcmTicketPrices: true,
    },
  });

  const ticketCategory = await prisma.ticketCategory.create({
    data: {
      name: category.name,
      quantity: newCategory.dtcmSections.reduce(
        (sum, s) => sum + s.capacity,
        0
      ),
      isSharedQuantity: true,
      eventId,
      dtcmSections: {
        connect: newCategory.dtcmSections.map((section) => ({
          id: section.id,
        })),
      },
    },
  });

  await linkTicketCategoryToDtcmSections(
    ticketCategory.id,
    newCategory.dtcmSections.map((section) => section.id)
  );

  for (const ticketPrice of newCategory.dtcmTicketPrices) {
    const bookableItem = await prisma.bookableItem.create({
      data: {
        type: BookableItemType.TICKET,
        eventId,
        price: ticketPrice.price,

        visibility:
          ticketPrice.state === "Available"
            ? Visibility.PUBLISHED
            : Visibility.DRAFT,
        privacy: Privacies.PUBLIC,
        saleStartDate: new Date(data.webSaleStartDate),
        saleEndDate: new Date(data.webSaleEndDate),
        translations: {
          create: languages.map((lang) => ({
            name: ticketPrice.name,
            description: ticketPrice.name,
            language: { connect: { id: lang.id } },
          })),
        },
      },
    });

    await prisma.ticket.create({
      data: {
        eventId,
        dtcmTicketPriceId: ticketPrice.id,
        categoryId: ticketCategory.id,
        bookableItemId: bookableItem.id,
      },
    });
  }
};
export const syncCategoryCapacity = async (
  existingCategory: any,
  category: any,
  data: any
) => {
  const newCapacity = data.sections
    .filter((s: any) => s.categoryId === category.id)
    .reduce((sum: number, sec: any) => sum + sec.capacity, 0);

  const existingCapacity = existingCategory.dtcmSections.reduce(
    (sum: number, sec: any) => sum + sec.capacity,
    0
  );

  if (newCapacity !== existingCapacity) {
    await prisma.ticketCategory.updateMany({
      where: {
        dtcmSections: {
          some: {
            id: { in: existingCategory.dtcmSections.map((s: any) => s.id) },
          },
        },
      },
      data: { quantity: newCapacity },
    });
  }
};
export const syncSections = async (
  existingCategory: any,
  category: any,
  data: any
) => {
  const existingSectionIds = existingCategory.dtcmSections.map(
    (section: { id: string }) => section.id
  );
  const ticketCategory =
    await findTicketCategoryByDtcmSections(existingSectionIds);

  for (const section of data.sections.filter(
    (s: any) => s.categoryId === category.id
  )) {
    const existingSection = existingCategory.dtcmSections.find(
      (sec: any) => sec.code === section.code
    );

    if (!existingSection) {
      await prisma.dTCMSection.create({
        data: {
          code: section.code,
          name: section.name,
          capacity: section.capacity,
          holds: section.holds,
          comps: section.comps,
          isGA: section.isGA,
          categoryId: existingCategory.id,
          ...(ticketCategory && { ticketCategoryId: ticketCategory.id }),
        },
      });
    } else if (existingSection.capacity !== section.capacity) {
      await prisma.dTCMSection.update({
        where: { id: existingSection.id },
        data: { capacity: section.capacity },
      });
    }
  }
};
export const syncTicketPrices = async (
  existingCategory: any,
  category: any,
  data: any,
  eventId: string,
  languages: any[]
) => {
  for (const ticketPrice of data.ticketPrices.filter(
    (p: any) => p.categoryId === category.id
  )) {
    const existingTicketPrice = existingCategory.dtcmTicketPrices.find(
      (tp: any) => tp.priceTypeId === String(ticketPrice.typeId)
    );

    const priceType = data.priceTypes.find(
      (pt: any) => pt.id === ticketPrice.typeId
    );

    if (!existingTicketPrice) {
      const newTicketPrice = await prisma.dTCMTicketPrice.create({
        data: {
          priceTypeId: String(ticketPrice.typeId),
          typeCode: ticketPrice.typeCode,
          price: ticketPrice.price,
          name: priceType?.name,
          state: priceType?.state,
          categoryId: existingCategory.id,
        },
      });

      const ticketCategory = await prisma.ticketCategory.findFirst({
        where: {
          dtcmSections: {
            some: {
              id: { in: existingCategory.dtcmSections.map((s: any) => s.id) },
            },
          },
        },
      });

      const bookableItem = await prisma.bookableItem.create({
        data: {
          type: BookableItemType.TICKET,
          eventId,
          price: ticketPrice.price,
          visibility:
            priceType?.state === "Available"
              ? Visibility.PUBLISHED
              : Visibility.DRAFT,
          privacy: Privacies.PUBLIC,
          saleStartDate: new Date(data.webSaleStartDate),
          saleEndDate: new Date(data.webSaleEndDate),
          translations: {
            create: languages.map((lang) => ({
              name: priceType?.name,
              description: priceType?.name,
              language: { connect: { id: lang.id } },
            })),
          },
        },
      });

      await prisma.ticket.create({
        data: {
          eventId,
          dtcmTicketPriceId: newTicketPrice.id,
          categoryId: ticketCategory?.id!,
          bookableItemId: bookableItem.id,
        },
      });
    } else {
      if (
        existingTicketPrice.price !== ticketPrice.price ||
        existingTicketPrice.state !== priceType?.state
      ) {
        await prisma.dTCMTicketPrice.update({
          where: { id: existingTicketPrice.id },
          data: {
            price: ticketPrice.price,
            state: priceType?.state,
          },
        });

        const tickets = await prisma.ticket.findMany({
          where: { dtcmTicketPriceId: existingTicketPrice.id },
          select: { bookableItemId: true },
        });

        await prisma.bookableItem.updateMany({
          where: { id: { in: tickets.map((t) => t.bookableItemId) } },
          data: {
            price: ticketPrice.price,
            visibility:
              priceType?.state === "Available"
                ? Visibility.PUBLISHED
                : Visibility.DRAFT,
          },
        });
      }
    }
  }
};
